import {
  ClockInCheckboxCheckedSVG,
  ClockInCheckboxUncheckedSVG,
  TrashSVG,
} from "@/assets/svg";
import { DatePicker, SelectWithRadio, TimePicker } from "@/components/form";
import Modal from "@/components/Modal";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import moment from "moment";
import React, { useEffect, useMemo, useRef, useState } from "react";

const ColumnTimePicker = ({ info, onDateTimeChange, formKey }: any) => {
  return (
    <div className="relative w-full">
      <TimePicker
        label=""
        value={info.getValue()}
        containerClassName="mb-0"
        onChange={(value) =>
          onDateTimeChange(info.row.original.id, formKey, value)
        }
        showAbove={true}
        className="border-0 outline-none pl-4"
        placeholder="Select time"
        required
      />
    </div>
  );
};

const ColumnDuration = ({ info, onDateTimeChange, rowId }: any) => {
  // Create refs to track input element and cursor position
  const inputRef = useRef<HTMLInputElement | null>(null);
  const cursorPosition = useRef<number | null>(null);
  const hasFocus = useRef<boolean>(false);
  const [internalValue, setInternalValue] = useState(info.getValue());

  // Effect to restore cursor position and focus after render
  useEffect(() => {
    const input = inputRef.current;
    const position = cursorPosition.current;
    const shouldRestoreFocus = input && position !== null && hasFocus.current;

    if (shouldRestoreFocus) {
      // Restore cursor position
      input.selectionStart = position;
      input.selectionEnd = position;

      // Restore focus if this input should have focus
      if (document.activeElement !== input) {
        input.focus();
      }
    }
  }, [internalValue]); // Re-run when internalValue changes

  // Sync external value with internal state
  useEffect(() => {
    setInternalValue(info.getValue());
  }, [info.getValue()]);

  const allTimeFieldsFilled = useMemo(() => {
    return (
      info.row.original.clockInDate &&
      info.row.original.clockInTime &&
      info.row.original.clockOutDate &&
      info.row.original.clockOutTime
    );
  }, [info.row.original]);

  return (
    <div className="px-3 flex items-center justify-start">
      {allTimeFieldsFilled ? (
        <span
          className="text-[#1E1E1E] font-medium"
          title="Auto-calculated from time entries"
        >
          {info.getValue()} Value
        </span>
      ) : (
        <input
          type="text"
          ref={inputRef}
          value={internalValue}
          className="w-full focus:outline-none text-[#1E1E1E] text-[16px] font-normal border-0"
          onChange={(e) => {
            // Store current cursor position before update
            cursorPosition.current = e.target.selectionStart;
            const newValue = e.target.value;
            setInternalValue(newValue);
          }}
          onFocus={() => {
            // Track that this input has focus
            hasFocus.current = true;
          }}
          onBlur={() => {
            // Track that this input lost focus
            hasFocus.current = false;

            // Commit the final value when input loses focus
            onDateTimeChange(rowId, "duration", internalValue);
          }}
          placeholder="00:00"
          title="Enter duration manually (format: HH:MM)"
        />
      )}
    </div>
  );
};

const ColumnNotes = ({ info, onNotesChange, rowId }: any) => {
  // Create refs to track input element and cursor position
  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  const cursorPosition = useRef<number | null>(null);
  const hasFocus = useRef<boolean>(false);
  const [internalValue, setInternalValue] = useState(info.getValue());
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Effect to restore cursor position and focus after render
  useEffect(() => {
    const input = inputRef.current;
    const position = cursorPosition.current;
    const shouldRestoreFocus = input && position !== null && hasFocus.current;

    if (shouldRestoreFocus) {
      // Restore cursor position
      input.selectionStart = position;
      input.selectionEnd = position;

      // Restore focus if this input should have focus
      if (document.activeElement !== input) {
        input.focus();
      }
    }
  }, [internalValue]); // Re-run when internalValue changes

  // Sync external value with internal state
  useEffect(() => {
    setInternalValue(info.getValue());
  }, [info.getValue()]);

  return (
    <div className="px-4">
      <textarea
        className="w-full focus:outline-none text-[#1E1E1E] text-[14px] font-medium"
        placeholder="Add notes"
        ref={(el) => {
          // Store the ref and adjust height when element is available
          if (el) {
            // Use requestAnimationFrame to avoid layout thrashing
            requestAnimationFrame(() => {
              // Reset to minimum height first
              el.style.height = "18px";
              // Set to scrollHeight to fit content
              el.style.height = `${el.scrollHeight}px`;
            });
          }
        }}
        value={internalValue}
        onChange={(e) => {
          // Update the value

          setInternalValue(e.target.value);

          // Use requestAnimationFrame for smooth height adjustment
          requestAnimationFrame(() => {
            const target = e.target;
            target.style.height = "18px";
            target.style.height = `${target.scrollHeight}px`;
          });
        }}
        onFocus={() => {
          // Track that this input has focus
          hasFocus.current = true;
        }}
        style={{
          minHeight: "18px",
          // height: "18px",
          resize: "none",
          overflow: "hidden",
        }}
        onBlur={() => {
          // Track that this input lost focus
          hasFocus.current = false;

          // Clear any pending debounce timer
          if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
            debounceTimerRef.current = null;
          }

          // Commit the final value when input loses focus
          onNotesChange(rowId, internalValue);
        }}
      />
    </div>
  );
};

export interface TimeEntryRow {
  id: string;
  clockInDate: string;
  clockInTime: string;
  clockOutDate: string;
  clockOutTime: string;
  duration?: string;
  serviceItem: string;
  billable: boolean;
  notes: string;
  actions: string;
  entry?: "MANUAL" | "REALTIME";
}

// Define column meta type for TypeScript
type ColumnMeta = {
  isSticky?: boolean;
  width?: string;
};

interface TimeEntryTableProps {
  entries: TimeEntryRow[];
  serviceItems: { value: string; label: string }[];
  onDateTimeChange: (
    entryId: string,
    field: keyof Omit<TimeEntryRow, "id">,
    value: string
  ) => void;
  onServiceItemChange: (entryId: string, value: string) => void;
  onBillableChange: (entryId: string, checked: boolean) => void;
  onNotesChange: (entryId: string, value: string) => void;
  activeEntryId: string;
  setActiveEntryId: (id: string) => void;
  onDeleteEntry?: (entryId: string) => void;
}

const TimeEntryTable: React.FC<TimeEntryTableProps> = ({
  entries,
  serviceItems,
  onDateTimeChange,
  onServiceItemChange,
  onBillableChange,
  onNotesChange,
  activeEntryId,
  setActiveEntryId,
  onDeleteEntry,
}) => {
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [entryToDelete, setEntryToDelete] = useState<string | null>(null);

  const handleDeleteClick = (entryId: string, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent row selection when clicking delete
    setEntryToDelete(entryId);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = () => {
    if (entryToDelete && onDeleteEntry) {
      onDeleteEntry(entryToDelete);
    }
    setIsDeleteModalOpen(false);
    setEntryToDelete(null);
  };

  const handleCancelDelete = () => {
    setIsDeleteModalOpen(false);
    setEntryToDelete(null);
  };
  const columnHelper = createColumnHelper<TimeEntryRow>();

  const columns = useMemo(
    () => [
      columnHelper.accessor("clockInDate", {
        header: "Clock in date",
        cell: (info) => (
          <div className="relative w-full">
            <DatePicker
              label=""
              className="w-full border-0"
              containerClassName="mb-0"
              value={info.getValue()}
              onChange={(value) =>
                onDateTimeChange(info.row.original.id, "clockInDate", value)
              }
              showAbove={true}
              maxDate={moment().hour(12).minute(0).second(0).format("YYYY-MM-DD")}
              placeholder="Choose date"
              clearable
            />
          </div>
        ),
        meta: { width: "140px" } as ColumnMeta,
      }),
      columnHelper.accessor("clockInTime", {
        header: "Clock in time",
        cell: (info) => (
          <ColumnTimePicker
            info={info}
            onDateTimeChange={onDateTimeChange}
            formKey="clockInTime"
          />
        ),
        meta: { width: "120px" } as ColumnMeta,
      }),
      columnHelper.accessor("clockOutDate", {
        header: "Clock out date",
        cell: (info) => (
          <div className="relative">
            <DatePicker
              label=""
              className="w-full border-0"
              containerClassName="mb-0"
              value={info.getValue()}
              onChange={(value) =>
                onDateTimeChange(info.row.original.id, "clockOutDate", value)
              }
              maxDate={moment().hour(12).minute(0).second(0).format("YYYY-MM-DD")}
              showAbove={true}
              placeholder="Choose date"
              clearable
            />
          </div>
        ),
        meta: { width: "140px" } as ColumnMeta,
      }),
      columnHelper.accessor("clockOutTime", {
        header: "Clock out time",
        cell: (info) => (
          <ColumnTimePicker
            info={info}
            onDateTimeChange={onDateTimeChange}
            formKey="clockOutTime"
          />
        ),
        meta: { width: "130px" } as ColumnMeta,
      }),
      columnHelper.accessor("duration", {
        header: "Duration",
        cell: (info) => {
          return (
            <ColumnDuration
              info={info}
              onDateTimeChange={onDateTimeChange}
              rowId={info.row.original.id}
            />
          );
        },
        meta: { width: "102px" } as ColumnMeta,
      }),
      columnHelper.accessor("serviceItem", {
        header: "Service item",
        cell: (info) => (
          <div className="w-full">
            <SelectWithRadio
              options={serviceItems}
              value={info.getValue()}
              onChange={(value) =>
                onServiceItemChange(info.row.original.id, value)
              }
              placeholder="Select a service"
              label=""
              showAbove={true}
              className="w-full border-0"
              labelClassName="truncate max-w-[160px] block"
            />
          </div>
        ),
        meta: { width: "180px" } as ColumnMeta,
      }),
      columnHelper.accessor("billable", {
        header: "Billable to HLJ",
        cell: (info) => (
          <div className="flex px-4">
            <div
              className="cursor-pointer"
              onClick={() =>
                onBillableChange(info.row.original.id, !info.getValue())
              }
            >
              {info.getValue() ? (
                <ClockInCheckboxCheckedSVG />
              ) : (
                <ClockInCheckboxUncheckedSVG />
              )}
            </div>
          </div>
        ),
        meta: { width: "80px" } as ColumnMeta,
      }),
      columnHelper.accessor("notes", {
        header: "Notes",
        cell: (info) => {
          return (
            <ColumnNotes
              info={info}
              rowId={info.row.original.id}
              onNotesChange={onNotesChange}
            />
          );
        },
        meta: { width: "200px" } as ColumnMeta,
      }),
      columnHelper.accessor("actions", {
        header: "",
        cell: (info) => {
          // Hide trash icon for the first entry (index 0)
          const rowIndex = info.row.index;
          return rowIndex === 0 ? (
            <div className="px-4"></div>
          ) : (
            <div
              className="px-4 cursor-pointer"
              onClick={(e) => handleDeleteClick(info.row.original.id, e)}
            >
              <TrashSVG />
            </div>
          );
        },
        meta: { width: "40px" } as ColumnMeta,
      }),
    ],
    [
      serviceItems,
      onDateTimeChange,
      onServiceItemChange,
      onBillableChange,
      onNotesChange,
    ]
  );

  const table = useReactTable({
    data: entries,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div className="overflow-x-auto relative py-4">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-[#F4F7F9] text-left">
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => {
                const isSticky = (header.column.columnDef.meta as any)
                  ?.isSticky;

                return (
                  <th
                    key={header.id}
                    scope="col"
                    className={`p-3 text-sm font-[600] text-[#1E1E1E] ${
                      isSticky
                        ? "sticky right-0 bg-[#F4F7F9] z-10 shadow-[0px_4px_4px_0px_rgba(0,0,0,0.25)]"
                        : ""
                    }`}
                    style={{
                      width: (header.column.columnDef.meta as ColumnMeta)
                        ?.width,
                      minWidth: (header.column.columnDef.meta as ColumnMeta)
                        ?.width,
                      maxWidth: (header.column.columnDef.meta as ColumnMeta)
                        ?.width,
                    }}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </th>
                );
              })}
            </tr>
          ))}
        </thead>
        <tbody className="bg-white divide-y divide-gray-200 border-l-1 border-r-1 border-b-1 border-gray-200">
          {table.getRowModel().rows.map((row) => (
            <tr
              key={row.id}
              className={`${
                row.original.id === activeEntryId ? "" : ""
              } border-b-1 border-l-1 border-r-1 border-[#E0E0E0]`}
              onClick={() => setActiveEntryId(row.original.id)}
            >
              {row.getVisibleCells().map((cell) => {
                const isSticky = (cell.column.columnDef.meta as ColumnMeta)
                  ?.isSticky;
                return (
                  <td
                    key={cell.id}
                    style={{
                      width: (cell.column.columnDef.meta as ColumnMeta)?.width,
                      minWidth: (cell.column.columnDef.meta as ColumnMeta)
                        ?.width,
                      maxWidth: (cell.column.columnDef.meta as ColumnMeta)
                        ?.width,
                    }}
                    className={`text-left h-[42px] border-r-1 border-[#E0E0E0]`}
                  >
                    {isSticky ? (
                      <div className="p-3 bg-white">
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </div>
                    ) : (
                      flexRender(cell.column.columnDef.cell, cell.getContext())
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>

      <Modal
        isOpen={isDeleteModalOpen}
        onClose={handleCancelDelete}
        title="Delete entry"
        width="min(95%, 550px)"
      >
        <div className="py-4">
          <p className="text-center text-[18px] font-[500] text-[#212121] mb-6">
            Are you sure you want to delete the entry ?
          </p>
          <div className="flex justify-center gap-4">
            <button
              onClick={handleConfirmDelete}
              className="px-3 py-2 w-[98px] text-[14px] font-medium h-[36px] bg-[#73B5C5] text-white rounded-md hover:bg-[#5da7b8] transition-colors cursor-pointer"
            >
              Yes
            </button>
            <button
              onClick={handleCancelDelete}
              className="px-3 py-2 w-[98px] h-[36px] text-[14px] font-medium bg-[#F5F5F5] text-gray-800 rounded-md hover:bg-gray-200 transition-colors cursor-pointer"
            >
              Cancel
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default TimeEntryTable;
